/**
 * The balance walk itself: where a key's stock crosses zero, and what it reads at a given week.
 *
 * Two consumers only: `engine.ts` (additive components) and the stockout engine in
 * `calculateStockoutTimes.ts` (stockout dates and suggested orders). A lint rule points anyone else
 * wanting stock levels at `projectPeriods`.
 */
import { largest, sortByWeekDiff, sumBy } from "~/utils/tsUtils"

import {
  demandAheadOfToday,
  getDemandConsumedByWeek,
  getElapsedWeekDemand,
  getWeekOfReachingDemand,
} from "./curve"

export function calculateStockOutWeekDiff(
  forecasts: number[],
  stockLevel: number,
  sortedIncomingOrders: { weekDiff: number; quantity: number }[],
  todayShift: number,
  salesWeekToDate?: number,
) {
  const stockOutWeekDiffs: number[] = []

  if (forecasts[forecasts.length - 1] === 0) {
    return stockOutWeekDiffs
  }
  let stock = stockLevel
  // Demand consumed before the anchor (today, or a restock arrival); subtracting it turns
  // cumulative demand values into demand ahead of the anchor.
  let anchorDemand = getElapsedWeekDemand(forecasts, todayShift, salesWeekToDate)

  const demandUpTo = (week: number) =>
    getDemandConsumedByWeek(forecasts, week, todayShift, salesWeekToDate)
  const weekOfDemand = (demand: number) =>
    getWeekOfReachingDemand(forecasts, demand, todayShift, salesWeekToDate)

  for (const order of sortedIncomingOrders) {
    // Arrival test and the crossing it records read one curve, but `weekOfDemand` is only the
    // inverse of `demandUpTo` up to float error: stock running out exactly at an arrival makes the
    // test fire on a ~1e-15 excess and the crossing invert one ULP past the arrival. Clamping keeps
    // the arrival inside `calculateStockLevelAtWeek`'s [stockout, week] restock window, which would
    // otherwise come back empty and drop the PO entirely.
    if (order.weekDiff > 0 && demandUpTo(order.weekDiff) - anchorDemand > stock) {
      stockOutWeekDiffs.push(Math.min(weekOfDemand(stock + anchorDemand), order.weekDiff))
      anchorDemand = demandUpTo(order.weekDiff)
      stock = order.quantity
    } else {
      stock += order.quantity
    }
  }
  stockOutWeekDiffs.push(weekOfDemand(stock + anchorDemand))

  return stockOutWeekDiffs
}

export function calculateStockLevelAtWeeks(
  weeks: number[],
  forecasts: number[],
  incomingOrders: { weekDiff: number; quantity: number }[] | null,
  currentStockLevel: number,
  stockWeek: number,
  salesWeekToDate?: number,
) {
  if (!forecasts.length && !incomingOrders?.length) {
    return weeks.map(() => currentStockLevel)
  }

  if (!forecasts.length) {
    return weeks.map(
      (w) =>
        currentStockLevel +
        (sumBy(incomingOrders?.filter((o) => o.weekDiff <= w) ?? [], "quantity") ?? 0),
    )
  }

  if (!incomingOrders?.length) {
    const elapsed = getElapsedWeekDemand(forecasts, stockWeek, salesWeekToDate)
    return weeks.map((w) =>
      Math.max(
        currentStockLevel -
          Math.max(getDemandConsumedByWeek(forecasts, w, stockWeek, salesWeekToDate) - elapsed, 0),
        0,
      ),
    )
  }

  // calculateStockLevelAtWeek anchors the post-stockout drain on nextOrders[0] in array order,
  // and callers concatenate returns and simulated orders onto already-sorted PO rows.
  const orders = sortByWeekDiff(incomingOrders)

  const stockoutDates = calculateStockOutWeekDiff(
    forecasts,
    currentStockLevel,
    orders,
    stockWeek,
    salesWeekToDate,
  )

  const out: number[] = []

  weeks.forEach((w) => {
    out.push(
      calculateStockLevelAtWeek(
        w,
        forecasts,
        orders,
        currentStockLevel,
        stockoutDates,
        stockWeek,
        salesWeekToDate,
      ),
    )
  })

  return out
}

export function calculateStockLevelAtWeek(
  week: number,
  forecasts: number[],
  incomingOrders: { weekDiff: number; quantity: number }[],
  currentStockLevel: number,
  stockOutDates: number[],
  stockWeek: number,
  salesWeekToDate?: number,
) {
  const lastStockOutWeek = stockOutDates.findLast((d) => d < week)

  if (lastStockOutWeek !== undefined) {
    const nextOrders = incomingOrders.filter(
      (order) => order.weekDiff >= lastStockOutWeek && order.weekDiff <= week,
    )
    if (!nextOrders.length) {
      return 0
    }
    // Split demand-from-today at the restock rather than re-reading the curve from the arrival:
    // both ends then come off the same curve as the crossings and the engine's `demand` component,
    // so the parts reconcile to the level exactly.
    const demandFromToday = largest(
      demandAheadOfToday(forecasts, week, stockWeek, salesWeekToDate),
      0,
    )
    const demandBeforeRestock = largest(
      demandAheadOfToday(forecasts, nextOrders[0]?.weekDiff ?? 0, stockWeek, salesWeekToDate),
      0,
    )
    return largest(
      sumBy(nextOrders, "quantity") - largest(demandFromToday - demandBeforeRestock, 0),
      0,
    )
  }

  return largest(
    sumBy(
      incomingOrders.filter((order) => order.weekDiff <= week),
      "quantity",
    ) +
      currentStockLevel -
      largest(demandAheadOfToday(forecasts, week, stockWeek, salesWeekToDate), 0),
    0,
  )
}
